import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import {
  Loader2,
  Share2,
  WifiOff,
  AlertTriangle,
  Users,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code2,
  Undo2,
  Redo2,
  Link2,
  Link2Off,
  History,
  MessageSquare,
  PaintBucket,
  ImageIcon,
  ListChecks,
  Minus,
  Palette,
  Check,
  RefreshCw,
  AlertCircle,
  Save,
  FileText,
  Hash,
} from 'lucide-react';
import { docsApi } from '../api/docs';
import { usersApi } from '../api/users';
import { versionsApi } from '../api/versions';
import { getWsToken } from '../api/auth';
import { getAccessToken } from '../api/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '../components/Toast';
import { getCollaboratorColor, getInitials } from '../lib/utils';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { ShareLinkDialog } from '../components/ShareLinkDialog';
import { VersionHistory } from '../components/VersionHistory';
import { CommentSection } from '../components/CommentSection';

/** 直连 Worker 的 WebSocket URL */
const COLLAB_WS_BASE = 'wss://xs.xiniu.qzz.io/api/docs';

/** 自动保存间隔（毫秒） */
const AUTOSAVE_INTERVAL = 30000;

/** 保存重试最大次数 */
const RETRY_MAX_ATTEMPTS = 3;

/** 内容变更防抖保存延迟（毫秒） */
const DEBOUNCE_SAVE_DELAY = 2000;

/** 网络重连检查间隔（毫秒） */
const RECONNECT_INTERVAL = 10000;

/** Yjs 同步超时：超过此时间仍未 sync 则回退到 REST 加载（毫秒） */
const SYNC_TIMEOUT_MS = 5000;

/** 自动版本创建间隔（毫秒） */
const AUTO_VERSION_INTERVAL = 10 * 60 * 1000;

type CollabStatus = 'connecting' | 'connected' | 'offline' | 'error';
type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

interface AwarenessUser {
  name: string;
  color: string;
  clientId: number;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`toolbar-btn ${active ? 'active' : ''}`}
    >
      {children}
    </button>
  );
}

/** 工具组：为内部按钮提供圆角分组背景 */
function ToolbarGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="toolbar-group">
      {children}
    </div>
  );
}

/** 预定义颜色 */
const TEXT_COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc',
  '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff',
  '#9900ff', '#ff00ff', '#e69138', '#6aa84f', '#3c78d8', '#a64d79',
];

const HIGHLIGHT_COLORS = [
  '#ffffff', '#ffff00', '#00ff00', '#00ffff', '#ff9900', '#ff00ff',
  '#ff0000', '#0000ff', '#999999', '#ffd966', '#b6d7a8', '#a2c4c9',
  '#9fc5e8', '#b4a7d6', '#d5a6bd', '#cccccc',
];

/** 斜杠命令条目 */
interface SlashCommandItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  action: (editor: NonNullable<ReturnType<typeof useEditor>>) => void;
}

export function DocEditorPage() {
  const { docId = '' } = useParams<{ docId: string }>();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('未命名文档');
  const [titleDirty, setTitleDirty] = useState(false);
  // titleDirty 的 ref 镜像，供 useEffect 读取最新值而不触发重跑
  const titleDirtyRef = useRef(false);
  titleDirtyRef.current = titleDirty;
  const [collabStatus, setCollabStatus] = useState<CollabStatus>('connecting');
  const [collaborators, setCollaborators] = useState<AwarenessUser[]>([]);
  const [showWsWarning, setShowWsWarning] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashMenuIndex, setSlashMenuIndex] = useState(0);
  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [highlightPopoverOpen, setHighlightPopoverOpen] = useState(false);

  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const lastSavedContentRef = useRef<string>('');
  // 编辑器实例 ref，类似思维导图的 meRef，用于保存时直接获取最新内容
  const editorInstanceRef = useRef<Editor | null>(null);
  const slashPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const slashStartPosRef = useRef<number | null>(null);
  // slashMenuOpen 的 ref 镜像，供 useEditor onUpdate 闭包读取最新值
  const slashMenuOpenRef = useRef(false);
  // 自上次版本创建后是否有编辑（用于自动版本触发）
  const hasEditedSinceVersionRef = useRef(false);
  // 镜像 slashMenuOpen 最新值到 ref（useEditor 闭包无法读到最新 state）
  slashMenuOpenRef.current = slashMenuOpen;
  // handleContentChange 的 ref 镜像，确保 useEditor onUpdate 闭包始终调用最新的函数
  const handleContentChangeRef = useRef<() => void>(() => {});
  // 该赋值在 render 阶段执行，此时 handleContentChange 已定义

  // 加载当前用户
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: usersApi.me,
  });

  // 加载文档元数据
  const { data: docMeta, isLoading: metaLoading } = useQuery({
    queryKey: ['doc', docId],
    queryFn: () => docsApi.get(docId),
    enabled: !!docId,
  });

  useEffect(() => {
    // 仅在 docMeta 变化时同步；用户正在输入（titleDirty）时不覆盖
    if (docMeta && !titleDirtyRef.current) {
      setTitle(docMeta.title || '未命名文档');
    }
  }, [docMeta]);

  // 创建 Yjs 文档 + WebSocket Provider
  const { ydoc, provider } = useMemo(() => {
    if (!docId) return { ydoc: null, provider: null };
    const doc = new Y.Doc();
    const token = getWsToken();
    const roomname = token
      ? `${docId}/collab?token=${encodeURIComponent(token)}`
      : `${docId}/collab`;
    const p = new WebsocketProvider(COLLAB_WS_BASE, roomname, doc, {
      connect: true,
    });
    return { ydoc: doc, provider: p };
  }, [docId]);

  // 监听连接状态
  useEffect(() => {
    if (!provider) return;
    const onStatus = (event: { status: 'connecting' | 'connected' | 'disconnected' }) => {
      if (event.status === 'connected') {
        setCollabStatus('connected');
        setShowWsWarning(false);
      } else if (event.status === 'connecting') {
        setCollabStatus('connecting');
      } else {
        setCollabStatus('offline');
      }
    };
    const onAwareness = () => {
      const states = Array.from(provider.awareness.getStates().entries() as Iterable<[number, unknown]>);
      const list: AwarenessUser[] = states
        .map(([clientId, state]) => {
          const u = (state as Record<string, unknown>)?.user as { name?: string; color?: string } | undefined;
          if (!u) return null;
          return {
            clientId,
            name: u.name ?? '匿名',
            color: u.color ?? '#6B7280',
          };
        })
        .filter((x): x is AwarenessUser => x !== null);
      setCollaborators(list);
    };
    provider.on('status', onStatus);
    provider.awareness.on('change', onAwareness);

    const warnTimer = setTimeout(() => {
      if (provider.ws?.readyState !== WebSocket.OPEN) {
        setShowWsWarning(true);
        setCollabStatus((s) => (s === 'connected' ? s : 'offline'));
      }
    }, 5000);

    return () => {
      provider.off('status', onStatus);
      provider.awareness.off('change', onAwareness);
      clearTimeout(warnTimer);
      provider.destroy();
    };
  }, [provider]);

  // 自动重连 WebSocket
  useEffect(() => {
    if (!provider) return;
    const interval = setInterval(() => {
      if (
        provider.ws?.readyState !== WebSocket.OPEN &&
        provider.ws?.readyState !== WebSocket.CONNECTING
      ) {
        provider.connect();
        setCollabStatus('connecting');
      }
    }, RECONNECT_INTERVAL);
    return () => clearInterval(interval);
  }, [provider]);

  // 设置 awareness user
  useEffect(() => {
    if (!provider || !me) return;
    provider.awareness.setLocalStateField('user', {
      name: me.nickname || me.email.split('@')[0],
      color: getCollaboratorColor(me.id),
    });
  }, [provider, me]);

  // 保存函数（参考思维导图 saveToRestApi 模式）
  const performSave = useCallback(async () => {
    const ed = editorInstanceRef.current;
    if (!ed) {
      console.warn('[DocEditor] performSave blocked: editor is null');
      return;
    }
    const html = ed.getHTML();
    if (html === lastSavedContentRef.current) {
      console.log('[DocEditor] performSave: content unchanged, skipping');
      setSaveStatus('saved');
      return;
    }
    console.log('[DocEditor] performSave: saving', html.length, 'chars');
    setSaveStatus('saving');
    setSaveError(null);

    // 指数退避重试（参考思维导图 saveToRestApi）
    try {
      await docsApi.saveContent(docId, html);
      console.log('[DocEditor] performSave: API success');
      lastSavedContentRef.current = html;
      setSaveStatus('saved');
      retryCountRef.current = 0;
    } catch (err) {
      retryCountRef.current++;
      const msg = err instanceof Error ? err.message : '保存失败';
      console.error('[DocEditor] performSave failed (attempt', retryCountRef.current, '):', msg);
      if (retryCountRef.current <= RETRY_MAX_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current - 1), 10000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        // 递归重试，每次重试都重新读取最新 HTML
        return performSave();
      }
      setSaveStatus('error');
      setSaveError(msg);
      toast.error(msg, '文档保存失败');
      retryCountRef.current = 0;
    }
  }, [docId]);

  // 内容变更防抖保存
  const handleContentChange = useCallback(() => {
    if (!editorInstanceRef.current) {
      console.warn('[DocEditor] handleContentChange blocked: editor is null');
      return;
    }
    console.log('[DocEditor] handleContentChange → unsaved, scheduling debounce');
    setSaveStatus('unsaved');
    hasEditedSinceVersionRef.current = true;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      console.log('[DocEditor] debounce fired → calling performSave');
      performSave();
    }, DEBOUNCE_SAVE_DELAY);
  }, [performSave]);

  // 同步 handleContentChange 到 ref，供 useEditor onUpdate 闭包使用
  handleContentChangeRef.current = handleContentChange;

  // 自动保存定时器（30 秒周期检查，脏数据才写入）
  useEffect(() => {
    saveTimerRef.current = setInterval(() => {
      if (editorInstanceRef.current) {
        performSave();
      }
    }, AUTOSAVE_INTERVAL);
    return () => {
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
  }, [performSave]);

  // 自动版本创建：有编辑则每 10 分钟创建一个自动版本
  useEffect(() => {
    if (!docId) return;
    const interval = setInterval(async () => {
      if (!hasEditedSinceVersionRef.current) return;
      try {
        await versionsApi.create(docId, { note: '自动版本' });
        hasEditedSinceVersionRef.current = false;
      } catch {
        // 静默失败，下个周期再试
      }
    }, AUTO_VERSION_INTERVAL);
    return () => clearInterval(interval);
  }, [docId]);

  // 页面关闭/隐藏时立即 flush HTML 备份到 R2（用 keepalive 确保请求完成）
  useEffect(() => {
    const flushSave = () => {
      const ed = editorInstanceRef.current;
      if (!ed) return;
      const html = ed.getHTML();
      if (html === lastSavedContentRef.current) return;
      const token = getAccessToken();
      fetch(`/api/docs/${docId}/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content: html }),
        keepalive: true,
      }).catch(() => {});
    };

    const onBeforeUnload = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      flushSave();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushSave();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [docId]);

  // 清理
  useEffect(() => {
    return () => {
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // 斜杠命令列表
  const slashCommands: SlashCommandItem[] = useMemo(() => [
    {
      id: 'heading1',
      label: '标题 1',
      icon: <Heading1 className="h-4 w-4" />,
      description: '大标题',
      action: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      id: 'heading2',
      label: '标题 2',
      icon: <Heading2 className="h-4 w-4" />,
      description: '中标题',
      action: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      id: 'heading3',
      label: '标题 3',
      icon: <Heading3 className="h-4 w-4" />,
      description: '小标题',
      action: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      id: 'bulletList',
      label: '无序列表',
      icon: <List className="h-4 w-4" />,
      description: '圆点列表',
      action: (editor) => editor.chain().focus().toggleBulletList().run(),
    },
    {
      id: 'orderedList',
      label: '有序列表',
      icon: <ListOrdered className="h-4 w-4" />,
      description: '编号列表',
      action: (editor) => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      id: 'taskList',
      label: '任务列表',
      icon: <ListChecks className="h-4 w-4" />,
      description: '带复选框的列表',
      action: (editor) => editor.chain().focus().toggleTaskList().run(),
    },
    {
      id: 'blockquote',
      label: '引用',
      icon: <Quote className="h-4 w-4" />,
      description: '引用文本',
      action: (editor) => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      id: 'codeBlock',
      label: '代码块',
      icon: <Code2 className="h-4 w-4" />,
      description: '代码片段',
      action: (editor) => editor.chain().focus().toggleCodeBlock().run(),
    },
    {
      id: 'horizontalRule',
      label: '分割线',
      icon: <Minus className="h-4 w-4" />,
      description: '水平分割线',
      action: (editor) => editor.chain().focus().setHorizontalRule().run(),
    },
    {
      id: 'image',
      label: '图片',
      icon: <ImageIcon className="h-4 w-4" />,
      description: '插入图片（URL）',
      action: () => setImageDialogOpen(true),
    },
  ], []);

  const filteredSlashCommands = useMemo(() => {
    if (!slashQuery) return slashCommands;
    const q = slashQuery.toLowerCase();
    return slashCommands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
    );
  }, [slashCommands, slashQuery]);

  // TipTap 编辑器
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          history: false,
          heading: { levels: [1, 2, 3] },
        }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Image.configure({ inline: true, allowBase64: false }),
        Placeholder.configure({
          placeholder: '按 / 打开命令菜单，开始写作…',
        }),
        Link.configure({
          openOnClick: true,
          HTMLAttributes: { class: 'text-accent underline underline-offset-2 hover:text-accent-hover' },
        }),
        ...(ydoc
          ? [
              Collaboration.configure({ document: ydoc }),
              CollaborationCursor.configure({
                provider,
                user: {
                  name: me?.nickname || me?.email?.split('@')[0] || '匿名',
                  color: me ? getCollaboratorColor(me.id) : '#6B7280',
                },
              }),
            ]
          : []),
      ],
      editorProps: {
        attributes: {
          class:
            'max-w-none focus:outline-none w-full leading-relaxed editor-content',
        },
      },
      onCreate: ({ editor: ed }) => {
        // 保存编辑器实例引用，供 performSave 直接获取最新 HTML
        editorInstanceRef.current = ed;
        // 新文档默认光标在首行
        ed.commands.setTextSelection(0);
        ed.commands.focus();
      },
      onUpdate: ({ editor: ed }) => {
        // 处理内容变更保存
        console.log('[DocEditor] onUpdate fired');
        handleContentChangeRef.current();

        // 斜杠命令检测
        const text = ed.getText();
        const { from } = ed.state.selection;
        const lineStart = text.lastIndexOf('\n', from - 1) + 1;
        const lineText = text.slice(lineStart, from);

        if (lineText.startsWith('/') && lineText.length > 1) {
          const query = lineText.slice(1);
          setSlashQuery(query);
          if (!slashMenuOpenRef.current) {
            setSlashMenuOpen(true);
            setSlashMenuIndex(0);
            slashStartPosRef.current = ed.state.doc.resolve(from).pos - lineText.length;
          }
          // 计算菜单位置
          const coords = ed.view.coordsAtPos(from);
          if (coords) {
            slashPosRef.current = { x: coords.left, y: coords.bottom + 4 };
          }
        } else if (lineText === '/') {
          setSlashQuery('');
          if (!slashMenuOpenRef.current) {
            setSlashMenuOpen(true);
            setSlashMenuIndex(0);
            slashStartPosRef.current = ed.state.doc.resolve(from).pos - 1;
          }
          const coords = ed.view.coordsAtPos(from);
          if (coords) {
            slashPosRef.current = { x: coords.left, y: coords.bottom + 4 };
          }
        } else if (slashMenuOpenRef.current) {
          setSlashMenuOpen(false);
          setSlashQuery('');
          setSlashMenuIndex(0);
          slashStartPosRef.current = null;
        }
      },
    },
    [ydoc, provider, me?.id],
  );

  // 加载已保存的内容
  // 优先 Yjs sync；sync 超时或 Yjs 为空时回退到 REST HTML，避免离线场景下编辑器空白
  useEffect(() => {
    if (!editor || !docId || !provider || !ydoc || contentLoaded) return;

    let cancelled = false;

    const tryLoadFromRest = async () => {
      if (cancelled || contentLoaded) return;
      try {
        const result = await docsApi.getContent(docId);
        if (cancelled || contentLoaded) return;
        if (result.content && result.content.trim()) {
          editor.commands.setContent(result.content, false);
          editor.commands.setTextSelection(0);
          editor.commands.focus();
          lastSavedContentRef.current = result.content;
        }
      } catch {
        // 没有已保存的内容是正常的，从空白开始
      } finally {
        if (!cancelled && !contentLoaded) setContentLoaded(true);
      }
    };

    const onSynced = (isSynced: boolean) => {
      if (!isSynced || cancelled || contentLoaded) return;
      const fragment = ydoc.getXmlFragment('prosemirror');
      if (fragment.length === 0) {
        tryLoadFromRest();
      } else {
        setContentLoaded(true);
        // 延迟一帧让编辑器完成渲染，然后设置光标到首行
        requestAnimationFrame(() => {
          if (!cancelled && editor) {
            editor.commands.setTextSelection(0);
            editor.commands.focus();
          }
        });
      }
    };

    provider.on('sync', onSynced);
    if (provider.synced) onSynced(true);

    // 同步超时兜底：WS 离线时 sync 永远不触发，超时后从 REST 加载
    const syncTimeout = setTimeout(() => {
      if (!cancelled && !contentLoaded) {
        tryLoadFromRest();
      }
    }, SYNC_TIMEOUT_MS);

    return () => {
      cancelled = true;
      provider.off('sync', onSynced);
      clearTimeout(syncTimeout);
    };
  }, [editor, docId, provider, ydoc, contentLoaded]);

  // 执行斜杠命令
  const executeSlashCommand = useCallback(
    (command: SlashCommandItem) => {
      if (!editor) return;
      // 删除 "/查询文本"
      const { from } = editor.state.selection;
      const startPos = slashStartPosRef.current ?? from;
      editor.chain().focus().deleteRange({ from: startPos, to: from }).run();
      command.action(editor);
      setSlashMenuOpen(false);
      setSlashQuery('');
      setSlashMenuIndex(0);
      slashStartPosRef.current = null;
    },
    [editor],
  );

  // 标题防抖保存
  function handleTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setTitle(next);
    setTitleDirty(true);
    if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    titleTimerRef.current = setTimeout(async () => {
      try {
        await docsApi.update(docId, { title: next.trim() || '未命名文档' });
        setTitleDirty(false);
        // 刷新 docMeta 缓存，让后续 docMeta 变化时拿到的是新标题
        void queryClient.invalidateQueries({ queryKey: ['doc', docId] });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '保存失败', '标题保存失败');
        // 失败也要清掉 dirty，否则 "保存中" 永远不消失
        setTitleDirty(false);
      }
    }, 500);
  }

  // 链接处理
  const openLinkDialog = useCallback(() => {
    const previousUrl = editor?.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setLinkDialogOpen(true);
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  // 图片插入
  const insertImage = useCallback(() => {
    if (!editor || !imageUrl) return;
    editor.chain().focus().setImage({ src: imageUrl }).run();
    setImageDialogOpen(false);
    setImageUrl('');
  }, [editor, imageUrl]);

  // 设置文本颜色
  const setTextColor = useCallback(
    (color: string) => {
      if (!editor) return;
      editor.chain().focus().setColor(color).run();
      setColorPopoverOpen(false);
    },
    [editor],
  );

  // 设置高亮颜色
  const setHighlightColor = useCallback(
    (color: string) => {
      if (!editor) return;
      if (color === '#ffffff') {
        editor.chain().focus().unsetHighlight().run();
      } else {
        editor.chain().focus().setHighlight({ color }).run();
      }
      setHighlightPopoverOpen(false);
    },
    [editor],
  );

  // 统计
  const fullText = editor?.getText() ?? '';
  const charCount = fullText.length;
  const wordCount = fullText
    .split(/[\s\n]+/)
    .filter(Boolean)
    .filter((w) => /[\u4e00-\u9fa5a-zA-Z0-9]/.test(w)).length;

  const statusConfig = {
    connecting: { color: 'bg-warning', text: '连接中' },
    connected: { color: 'bg-success', text: '已同步' },
    offline: { color: 'bg-danger', text: '离线模式' },
    error: { color: 'bg-danger', text: '连接错误' },
  } as const;
  const status = statusConfig[collabStatus];

  const saveStatusConfig = {
    saved: { icon: Check, color: 'text-success', bg: 'bg-success/10', text: '已保存' },
    saving: { icon: Loader2, color: 'text-warning', bg: 'bg-warning/10', text: '保存中…' },
    unsaved: { icon: Save, color: 'text-text-tertiary', bg: 'bg-bg-subtle', text: '未保存' },
    error: { icon: AlertCircle, color: 'text-danger', bg: 'bg-danger/10', text: '保存失败' },
  } as const;
  const saveCfg = saveStatusConfig[saveStatus];
  const SaveIcon = saveCfg.icon;

  if (metaLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-bg-canvas">
        <div className="editor-skeleton">
          <div className="editor-skeleton-icon">
            <Loader2 className="relative h-6 w-6 animate-spin text-accent" />
          </div>
          <span className="text-sm text-text-tertiary">文档加载中…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-bg-canvas">
      {/* 顶部 bar - Liquid Glass */}
      <div className="relative flex items-center justify-between border-b border-border/60 liquid-glass px-4 sm:px-6 py-3">
        <div className="topbar-highlight" aria-hidden />

        <div className="flex flex-1 items-center gap-3 min-w-0">
          {/* 文档图标 */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent-50 to-accent-100 shadow-xs ring-1 ring-accent/20">
            {docMeta?.emoji ? (
              <span aria-hidden className="text-lg">{docMeta.emoji}</span>
            ) : (
              <FileText className="h-5 w-5 text-accent" />
            )}
          </div>
          <input
            value={title}
            onChange={handleTitleChange}
            placeholder="未命名文档"
            className="flex-1 min-w-0 bg-transparent text-lg font-semibold tracking-tight text-text-primary placeholder:text-text-tertiary focus:outline-none truncate"
            aria-label="文档标题"
          />
          {titleDirty && (
            <span className="flex items-center gap-1 text-xs text-text-tertiary shrink-0">
              <span className="h-1 w-1 rounded-full bg-warning animate-pulse" />
              保存中
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* 协作者头像 stack */}
          {collaborators.length > 0 && (
            <div className="hidden sm:flex items-center -space-x-2" aria-label={`当前 ${collaborators.length} 位协作者`}>
              {collaborators.slice(0, 5).map((c) => (
                <span
                  key={c.clientId}
                  className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-bg-surface text-[10px] font-medium text-white shadow-xs ring-1 ring-black/5 transition-transform hover:translate-y-[-2px] hover:z-10"
                  style={{ backgroundColor: c.color }}
                  title={c.name}
                >
                  {getInitials(c.name)}
                </span>
              ))}
              {collaborators.length > 5 && (
                <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-bg-surface bg-bg-subtle text-[10px] font-medium text-text-secondary shadow-xs">
                  +{collaborators.length - 5}
                </span>
              )}
            </div>
          )}

          {/* 协同状态 chip */}
          <span
            className="hidden sm:flex items-center gap-1.5 rounded-full bg-bg-subtle/70 px-2.5 py-1 text-xs font-medium text-text-secondary"
            title={status.text}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${status.color} ${
                collabStatus === 'connecting' ? 'animate-pulse' : ''
              }`}
            />
            {status.text}
          </span>

          <button
            type="button"
            className="btn btn-primary btn-sm shadow-sm transition-all hover:shadow-md hover:-translate-y-px"
            onClick={() => setShareDialogOpen(true)}
          >
            <Share2 className="h-3.5 w-3.5" /> 分享
          </button>
        </div>
      </div>

      {/* WS 警告条 */}
      {showWsWarning && collabStatus !== 'connected' && (
        <div className="flex items-center gap-2 border-b border-warning/30 bg-gradient-to-r from-warning/10 via-warning/5 to-warning/10 px-4 sm:px-6 py-2 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">协同服务连接失败，当前为本地编辑模式（修改不会实时同步给他人）。</span>
          <button
            type="button"
            className="shrink-0 rounded px-2 py-0.5 font-medium underline-offset-2 hover:bg-warning/10 hover:underline"
            onClick={() => {
              provider?.connect();
              setCollabStatus('connecting');
            }}
          >
            重试
          </button>
        </div>
      )}

      {/* 保存错误提示条 */}
      {saveStatus === 'error' && saveError && (
        <div className="flex items-center gap-2 border-b border-danger/30 bg-gradient-to-r from-danger/10 via-danger/5 to-danger/10 px-4 sm:px-6 py-2 text-xs text-danger">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">保存失败：{saveError}</span>
          <button
            type="button"
            className="shrink-0 rounded px-2 py-0.5 font-medium underline-offset-2 hover:bg-danger/10 hover:underline"
            onClick={() => {
              retryCountRef.current = 0;
              performSave();
            }}
          >
            <RefreshCw className="h-3 w-3 inline mr-1" />
            重试
          </button>
        </div>
      )}

      {/* 格式化工具栏 - Liquid Glass sticky */}
      {editor && (
        <div className="sticky top-0 z-30 flex items-center gap-1.5 toolbar-glass px-4 sm:px-6 py-2 overflow-x-auto">
          <ToolbarGroup>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive('bold')}
              title="加粗 (Ctrl+B)"
            >
              <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive('italic')}
              title="斜体 (Ctrl+I)"
            >
              <Italic className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive('underline')}
              title="下划线 (Ctrl+U)"
            >
              <UnderlineIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              active={editor.isActive('strike')}
              title="删除线"
            >
              <Strikethrough className="h-4 w-4" />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              active={editor.isActive('heading', { level: 1 })}
              title="标题 1"
            >
              <Heading1 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              active={editor.isActive('heading', { level: 2 })}
              title="标题 2"
            >
              <Heading2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              active={editor.isActive('heading', { level: 3 })}
              title="标题 3"
            >
              <Heading3 className="h-4 w-4" />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              active={editor.isActive('bulletList')}
              title="无序列表"
            >
              <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              active={editor.isActive('orderedList')}
              title="有序列表"
            >
              <ListOrdered className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleTaskList().run()}
              active={editor.isActive('taskList')}
              title="任务列表"
            >
              <ListChecks className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              active={editor.isActive('blockquote')}
              title="引用"
            >
              <Quote className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              active={editor.isActive('codeBlock')}
              title="代码块"
            >
              <Code2 className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              title="分割线"
            >
              <Minus className="h-4 w-4" />
            </ToolbarButton>
          </ToolbarGroup>

          {/* 文本颜色 */}
          <Popover.Root open={colorPopoverOpen} onOpenChange={setColorPopoverOpen}>
            <Popover.Trigger asChild>
              <ToolbarGroup>
                <ToolbarButton
                  onClick={() => setColorPopoverOpen(true)}
                  active={editor.isActive('textStyle')}
                  title="文本颜色"
                >
                  <Palette className="h-4 w-4" />
                </ToolbarButton>
              </ToolbarGroup>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="z-50 w-52 rounded-xl border border-border bg-bg-surface/95 p-3 shadow-xl backdrop-blur-xl animate-fade-in-scale"
                side="bottom"
                align="start"
                sideOffset={6}
              >
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">文本颜色</div>
                <div className="grid grid-cols-6 gap-1.5">
                  {TEXT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="color-swatch flex h-6 w-6 items-center justify-center rounded-md border border-black/5 ring-1 ring-black/5"
                      style={{ backgroundColor: c }}
                      onClick={() => setTextColor(c)}
                      title={c}
                    >
                      <span className="text-[10px] font-bold" style={{ color: c === '#ffffff' ? '#000' : '#fff' }}>
                        A
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-2.5 w-full rounded-md py-1 text-xs text-text-secondary hover:bg-bg-subtle hover:text-text-primary transition-colors"
                  onClick={() => {
                    editor?.chain().focus().unsetColor().run();
                    setColorPopoverOpen(false);
                  }}
                >
                  清除颜色
                </button>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>

          {/* 高亮颜色 */}
          <Popover.Root open={highlightPopoverOpen} onOpenChange={setHighlightPopoverOpen}>
            <Popover.Trigger asChild>
              <ToolbarGroup>
                <ToolbarButton
                  onClick={() => setHighlightPopoverOpen(true)}
                  active={editor.isActive('highlight')}
                  title="高亮颜色"
                >
                  <PaintBucket className="h-4 w-4" />
                </ToolbarButton>
              </ToolbarGroup>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="z-50 w-52 rounded-xl border border-border bg-bg-surface/95 p-3 shadow-xl backdrop-blur-xl animate-fade-in-scale"
                side="bottom"
                align="start"
                sideOffset={6}
              >
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">高亮颜色</div>
                <div className="grid grid-cols-8 gap-1.5">
                  {HIGHLIGHT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="color-swatch h-6 w-6 rounded-md border border-black/5 ring-1 ring-black/5"
                      style={{ backgroundColor: c }}
                      onClick={() => setHighlightColor(c)}
                      title={c}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-2.5 w-full rounded-md py-1 text-xs text-text-secondary hover:bg-bg-subtle hover:text-text-primary transition-colors"
                  onClick={() => {
                    editor?.chain().focus().unsetHighlight().run();
                    setHighlightPopoverOpen(false);
                  }}
                >
                  清除高亮
                </button>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>

          <ToolbarGroup>
            <ToolbarButton
              onClick={() => setImageDialogOpen(true)}
              title="插入图片"
            >
              <ImageIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              onClick={openLinkDialog}
              active={editor.isActive('link')}
              title="插入链接"
            >
              <Link2 className="h-4 w-4" />
            </ToolbarButton>
            {editor.isActive('link') && (
              <ToolbarButton
                onClick={() => editor.chain().focus().unsetLink().run()}
                title="移除链接"
              >
                <Link2Off className="h-4 w-4" />
              </ToolbarButton>
            )}
          </ToolbarGroup>

          <div className="ml-auto flex items-center gap-0.5 shrink-0">
            <ToolbarGroup>
              <ToolbarButton
                onClick={() => setVersionHistoryOpen(true)}
                title="版本历史"
              >
                <History className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => setShowComments((v) => !v)}
                active={showComments}
                title="评论"
              >
                <MessageSquare className="h-4 w-4" />
              </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup>
              <ToolbarButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                title="撤销 (Ctrl+Z)"
              >
                <Undo2 className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                title="重做 (Ctrl+Y)"
              >
                <Redo2 className="h-4 w-4" />
              </ToolbarButton>
            </ToolbarGroup>
          </div>
        </div>
      )}

      {/* 编辑器 + 评论面板 */}
      <div className="flex flex-1 overflow-hidden">
        <div className="relative flex-1 bg-bg-canvas">
          {editor ? (
            <>
              <div className="editor-container">
                <EditorContent editor={editor} />
              </div>

              {/* 浮动保存状态徽章 - 仅在非已保存状态显示 */}
              {saveStatus !== 'saved' && (
                <div
                  className={`fixed bottom-20 right-6 z-40 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs shadow-lg backdrop-blur-xl transition-all duration-base save-status-badge ${
                    saveStatus === 'error'
                      ? 'border-danger/30 bg-danger/10 text-danger'
                      : saveStatus === 'saving'
                        ? 'border-warning/30 bg-warning/10 text-warning'
                        : 'border-border bg-bg-surface/90 text-text-secondary'
                  }`}
                >
                  <SaveIcon className={`h-3 w-3 ${saveStatus === 'saving' ? 'animate-spin' : ''}`} />
                  <span className="font-medium">{saveCfg.text}</span>
                </div>
              )}
            </>
          ) : (
            <div className="editor-skeleton">
              <div className="editor-skeleton-icon">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
              </div>
              <span className="text-sm">编辑器加载中…</span>
            </div>
          )}
        </div>
        {showComments && editor && docMeta && (
          <div className="w-80 shrink-0 border-l border-border/60 bg-bg-surface/50">
            <CommentSection
              docId={docId}
              wsId={docMeta.workspace_id}
              editor={editor}
              currentUserId={me?.id}
            />
          </div>
        )}
      </div>

      {/* 状态栏 */}
      <div className="status-bar">
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full bg-bg-subtle/70 px-2 py-0.5">
            <Users className="h-3.5 w-3.5" />
            {collaborators.length} 位在线
          </span>
          {collabStatus === 'offline' && (
            <span className="flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-danger">
              <WifiOff className="h-3.5 w-3.5" /> 离线
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full bg-bg-subtle/70 px-2 py-0.5" title="字数">
            <FileText className="h-3.5 w-3.5" />
            {wordCount} 字
          </span>
          <span className="flex items-center gap-1.5 rounded-full bg-bg-subtle/70 px-2 py-0.5" title="字符数">
            <Hash className="h-3.5 w-3.5" />
            {charCount} 字符
          </span>
        </span>
      </div>

      {/* 斜杠命令菜单 */}
      {slashMenuOpen && editor && filteredSlashCommands.length > 0 && (
        <div
          className="slash-menu fixed z-50 w-72 animate-fade-in-scale"
          style={{
            left: `${Math.min(slashPosRef.current.x, window.innerWidth - 300)}px`,
            top: `${Math.min(slashPosRef.current.y, window.innerHeight - 400)}px`,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSlashMenuOpen(false);
              setSlashQuery('');
              slashStartPosRef.current = null;
            }
          }}
        >
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            {slashQuery ? `匹配 “${slashQuery}”` : '快速插入'}
          </div>
          {filteredSlashCommands.map((cmd, idx) => (
            <button
              key={cmd.id}
              type="button"
              className={`slash-menu-item ${idx === slashMenuIndex ? 'active' : ''}`}
              onClick={() => executeSlashCommand(cmd)}
              onMouseEnter={() => setSlashMenuIndex(idx)}
            >
              <span className="slash-menu-item-icon">
                {cmd.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium leading-tight">{cmd.label}</div>
                <div className="text-[11px] text-text-tertiary truncate">{cmd.description}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* 链接弹窗 */}
      <Dialog.Root open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[440px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 dialog-content"
            aria-describedby={undefined}
          >
            <div className="dialog-accent-bar" aria-hidden />
            <div className="p-6">
              <Dialog.Title className="text-lg font-semibold tracking-tight">插入链接</Dialog.Title>
              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-text-secondary" htmlFor="link-url">
                  URL
                </label>
                <input
                  id="link-url"
                  type="url"
                  className="input"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setLink();
                  }}
                />
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setLinkDialogOpen(false)}
                  className="btn btn-ghost btn-md"
                >
                  取消
                </button>
                <button type="button" onClick={setLink} className="btn btn-primary btn-md shadow-sm">
                  确认
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 图片弹窗 */}
      <Dialog.Root open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[440px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 dialog-content"
            aria-describedby={undefined}
          >
            <div className="dialog-accent-bar" aria-hidden />
            <div className="p-6">
              <Dialog.Title className="text-lg font-semibold tracking-tight">插入图片</Dialog.Title>
              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-text-secondary" htmlFor="image-url">
                  图片 URL
                </label>
                <input
                  id="image-url"
                  type="url"
                  className="input"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.png"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') insertImage();
                  }}
                />
              </div>
              {imageUrl && (
                <div className="mt-3 flex items-center justify-center rounded-xl border border-border bg-bg-subtle p-3">
                  <img
                    src={imageUrl}
                    alt="预览"
                    className="max-h-32 max-w-full rounded-lg object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setImageDialogOpen(false);
                    setImageUrl('');
                  }}
                  className="btn btn-ghost btn-md"
                >
                  取消
                </button>
                <button type="button" onClick={insertImage} className="btn btn-primary btn-md shadow-sm">
                  插入
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 分享链接弹窗 */}
      {docMeta && (
        <ShareLinkDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          wsId={docMeta.workspace_id}
          docId={docId}
        />
      )}

      {/* 版本历史弹窗 */}
      <VersionHistory
        open={versionHistoryOpen}
        onOpenChange={setVersionHistoryOpen}
        docId={docId}
      />
    </div>
  );
}